import { LitElement, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import "@/components/ui/modal";
import "@/components/ui/pagination/pagination";
import "./components";
import { MONITORING_TABS } from "./types";
import { closeIcon, plusIcon, refreshCwIcon } from "@/assets/icons";
import {
  getMembers,
  getInvitations,
  removeMember,
  resendInvitation,
  cancelInvitation,
  getRoles,
} from "@/service";
import { Member, Invitation, GetRolesParams, Role } from "@/types";
import { InvitationFilters, MemberFilters } from "./components";

export class SbMemberManagement extends LitElement {
  @property({ type: String }) mode: "MODAL" | "PAGE" = "PAGE";
  @property({ type: String }) title = "Member Management";
  @property({ type: String }) description =
    "Manage member access, roles, and invitations";
  @property({ type: String }) organizationId: string = "";

  @state() private isOpen = false;
  @state() monitoringFlag: Boolean = false;
  @state() private membersSearchTerm: string = "";
  @state() private invitationsSearchTerm: string = "";
  @state() private members: Member[] = [];
  @state() private invitations: Invitation[] = [];
  @state() private membersLoading = false;
  @state() private invitationsLoading = false;
  @state() private useMockData = true;
  @state() private membersPage = 1;
  @state() private membersTotalPages = 0;
  @state() private invitationsPage = 1;
  @state() private invitationsTotalPages = 0;
  @state() private membersTotal = 0;
  @state() private invitationsTotal = 0;
  @state() private rowsPerPage = 5;
  private _membersSearchDebounceTimer: number | undefined;
  private _invitationsSearchDebounceTimer: number | undefined;
  @property({ type: String }) inviteDrawerId: string = "sb-member-invite";
  @state() private availableRoles: { value: string; label: string }[] = [];
  @state() private isLoadingRoles = false;
  private _rolesLoaded = false; // Flag to prevent double-loading

  // Filter state for members and invitations (extensible for future filters)
  @state() private memberFilters: MemberFilters = {
    roles: [],
  };
  @state() private invitationFilters: InvitationFilters = {
    roles: [],
    status: [], // empty means use default: pending,expired,cancelled
  };

  // Status options for invitation filter
  private invitationStatusOptions = [
    { value: "pending", label: "Pending" },
    { value: "accepted", label: "Accepted" },
    { value: "expired", label: "Expired" },
    { value: "cancelled", label: "Cancelled" },
  ];

  @state() private selectedMember: Member | null = null;
  @state() private actionModalOpen = false;
  @state() private actionModalConfig = {
    title: "",
    description: "",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    variant: "default" as "default" | "danger",
    onConfirm: () => {},
    onCancel: () => {},
    infoText: "",
  };

  private async openActionModal(config: {
    title: string;
    description: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "default" | "danger";
    onConfirm: () => Promise<void>;
    onCancel?: () => void;
    infoText?: string;
  }) {
    this.actionModalConfig = {
      ...this.actionModalConfig,
      ...config,
    };
    this.actionModalOpen = false;
    this.requestUpdate();
    await this.updateComplete;
    this.actionModalOpen = true;
  }

  @property({ type: String }) manageRolesDrawerId: string = "sb-manage-roles";

  private _handleOpen = () => {
    if (this.mode === "MODAL") {
      this.isOpen = true;
    }
  };

  private _handleClose = () => {
    if (this.mode === "MODAL") {
      this.isOpen = false;
    }
  };

  private tryRemoveFallback() {
    requestAnimationFrame(() => {
      const root = this as HTMLElement;
      const fallback = root.querySelector('[part="fallback"]');
      if (fallback) {
        fallback.remove();
      } else {
        console.warn("fallback not found");
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("open-member-management", this._handleOpen);
    window.addEventListener("close-member-management", this._handleClose);
    document.addEventListener("open-member-management", this._handleOpen);
    document.addEventListener("close-member-management", this._handleClose);
    window.addEventListener("invite-members-success", this.loadData);
    window.addEventListener("update-members-role", this.loadData);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("open-member-management", this._handleOpen);
    window.removeEventListener("close-member-management", this._handleClose);
    document.removeEventListener("open-member-management", this._handleOpen);
    document.removeEventListener("close-member-management", this._handleClose);
    window.removeEventListener("invite-members-success", this.loadData);
    window.removeEventListener("update-members-role", this.loadData);

    if (this._membersSearchDebounceTimer) {
      window.clearTimeout(this._membersSearchDebounceTimer);
    }
    if (this._invitationsSearchDebounceTimer) {
      window.clearTimeout(this._invitationsSearchDebounceTimer);
    }
  }

  protected firstUpdated() {
    this.isOpen = this.mode === "PAGE";
    this.loadData();
    if (this.organizationId && !this._rolesLoaded) {
      this.loadRoles();
    }
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has("mode")) {
      this.isOpen = this.mode === "PAGE";
    }
    if (
      changed.has("organizationId") &&
      this.organizationId &&
      !this._rolesLoaded
    ) {
      this.loadRoles();
    }
  }

  private async loadRoles() {
    if (!this.organizationId || this._rolesLoaded) return;
    try {
      this.isLoadingRoles = true;
      const params: GetRolesParams = {
        resource_id: this.organizationId,
        resource_type: "organization",
      };
      const response = await getRoles(params);
      this.availableRoles = response.data.map((role: Role) => ({
        value: role.key || role.name,
        label: role.name,
      }));
      this._rolesLoaded = true;
      // Optionally select all roles by default:
      // this.memberFilters = { ...this.memberFilters, roles: this.availableRoles.map(r => r.value) };
      // this.loadMembers(); // reload after setting default
    } catch (error) {
      console.error("Failed to load roles:", error);
    } finally {
      this.isLoadingRoles = false;
    }
  }

  private loadData = async () => {
    if (!this.organizationId) return;
    await this.loadMembers();
    await this.loadInvitations();
    this.tryRemoveFallback();
  };
  private async loadMembers(
    page: number = 1,
    limit: number = this.rowsPerPage,
    query?: string,
    filters?: Record<string, any>,
  ) {
    try {
      this.membersLoading = true;
      const params: any = { page, limit };
      if (query) params.query = query;

      // Apply filters
      const filtersToApply = filters || this.memberFilters;
      if (filtersToApply.roles && filtersToApply.roles.length) {
        params.roles = filtersToApply.roles.join(",");
      }
      const response = await getMembers(this.organizationId, params);

      this.members = response.data;
      this.membersPage = response.pagination.page;
      this.membersTotalPages = response.pagination.total_pages;
      this.membersTotal = response.pagination.total;
      this.useMockData = false;
    } catch (error) {
      console.error("[sb-member-management] Failed loading members:", error);
      this.useMockData = true;
    } finally {
      this.membersLoading = false;
    }
  }

  private async loadInvitations(
    page: number = 1,
    limit: number = this.rowsPerPage,
    query?: string,
    filters?: Record<string, any>,
  ) {
    try {
      this.invitationsLoading = true;
      const params: any = { page, limit };
      if (query) params.query = query;

      // Apply filters
      const filtersToApply = filters || this.invitationFilters;
      // Determine status: if filters.status is non-empty, use that; else default
      let statusParam = "pending,cancelled"; // default
      if (filtersToApply.status && filtersToApply.status.length > 0) {
        statusParam = filtersToApply.status.join(",");
      }
      params.status = statusParam;

      // Roles filter
      if (filtersToApply.roles && filtersToApply.roles.length) {
        params.roles = filtersToApply.roles.join(",");
      }

      const response = await getInvitations(this.organizationId, params);
      this.invitations = response.data;
      this.invitationsPage = response.pagination.page;
      this.invitationsTotalPages = response.pagination.total_pages;
      this.invitationsTotal = response.pagination.total;
      this.useMockData = false;
    } catch (error) {
      console.error(
        "[sb-member-management] Failed loading invitations:",
        error,
      );
      this.useMockData = true;
    } finally {
      this.invitationsLoading = false;
    }
  }

  private handleMembersPageChange(e: CustomEvent) {
    const page = e.detail.page;
    this.loadMembers(
      page,
      this.rowsPerPage,
      this.membersSearchTerm,
      this.memberFilters,
    );
  }

  private handleInvitationsPageChange(e: CustomEvent) {
    const page = e.detail.page;
    this.loadInvitations(
      page,
      this.rowsPerPage,
      this.invitationsSearchTerm,
      this.invitationFilters,
    );
  }

  private handleMembersSearchChange(event: CustomEvent) {
    this.membersSearchTerm = event.detail.searchTerm || "";
    this.loadMembers(
      1,
      this.rowsPerPage,
      this.membersSearchTerm.trim() ? this.membersSearchTerm : undefined,
      this.memberFilters,
    );
  }

  private handleInvitationsSearchChange(event: CustomEvent) {
    this.invitationsSearchTerm = event.detail.searchTerm || "";
    this.loadInvitations(
      1,
      this.rowsPerPage,
      this.invitationsSearchTerm.trim()
        ? this.invitationsSearchTerm
        : undefined,
      this.invitationFilters,
    );
  }

  private handleMembersFilterChange(event: CustomEvent) {
    const roles = event.detail.roles || [];
    this.memberFilters = { roles };
    this.loadMembers(
      1,
      this.rowsPerPage,
      this.membersSearchTerm,
      this.memberFilters,
    );
  }

  private handleInvitationsFilterChange(event: CustomEvent) {
    const filters = event.detail || {};
    this.invitationFilters = {
      roles: filters.roles || [],
      status: filters.status || [],
    };
    this.loadInvitations(
      1,
      this.rowsPerPage,
      this.invitationsSearchTerm,
      this.invitationFilters,
    );
  }

  private handleMembersClearFilters() {
    this.memberFilters = { roles: [] };
    this.loadMembers(
      1,
      this.rowsPerPage,
      this.membersSearchTerm,
      this.memberFilters,
    );
  }

  private handleInvitationsClearFilters() {
    this.invitationFilters = { roles: [], status: [] };
    this.loadInvitations(
      1,
      this.rowsPerPage,
      this.invitationsSearchTerm,
      this.invitationFilters,
    );
  }

  private handleMembersRemoveFilter(event: CustomEvent) {
    const filterValue = event.detail.filterValue;
    this.memberFilters = {
      ...this.memberFilters,
      roles: this.memberFilters.roles.filter((r: string) => r !== filterValue),
    };
    this.loadMembers(
      1,
      this.rowsPerPage,
      this.membersSearchTerm,
      this.memberFilters,
    );
  }

  private handleInvitationsRemoveFilter(event: CustomEvent) {
    const filterValue = event.detail.filterValue;
    this.invitationFilters = {
      ...this.invitationFilters,
      roles: this.invitationFilters.roles.filter(
        (r: string) => r !== filterValue,
      ),
      status: this.invitationFilters.status.filter(
        (status: string) => status !== filterValue,
      ),
    };
    this.loadInvitations(
      1,
      this.rowsPerPage,
      this.invitationsSearchTerm,
      this.invitationFilters,
    );
  }

  private handleMembersRowsPerPageChange(event: CustomEvent) {
    const rowsPerPage = event.detail.rowsPerPage;
    this.rowsPerPage = rowsPerPage;
    this.loadMembers(
      1,
      rowsPerPage,
      this.membersSearchTerm,
      this.memberFilters,
    );
  }

  private handleInvitationsRowsPerPageChange(event: CustomEvent) {
    const rowsPerPage = event.detail.rowsPerPage;
    this.rowsPerPage = rowsPerPage;
    this.loadInvitations(
      1,
      rowsPerPage,
      this.invitationsSearchTerm,
      this.invitationFilters,
    );
  }

  private handleRefresh(tab: "members" | "invitations") {
    if (tab === "members") {
      this.loadMembers(
        this.membersPage,
        this.rowsPerPage,
        this.membersSearchTerm,
        this.memberFilters,
      );
    } else {
      this.loadInvitations(
        this.invitationsPage,
        this.rowsPerPage,
        this.invitationsSearchTerm,
        this.invitationFilters,
      );
    }
  }

  private openManageRoles(event: CustomEvent) {
    const member = event.detail as Member;
    this.selectedMember = member;
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent("open-sidedrawer", {
          detail: { id: this.manageRolesDrawerId, side: "right" },
          bubbles: true,
          composed: true,
        }),
      );
    }, 50);
  }

  private handleSuspendMember(event: CustomEvent) {
    const member = event.detail as Member;
    this.openActionModal({
      title: "Suspend User",
      description: `Are you sure you want to suspend ${member.user?.name || member.user?.email} from this organization?`,
      confirmLabel: "Suspend User",
      variant: "danger",
      infoText:
        "They will lose access to the organization until they are reactivated.",
      onConfirm: async () => {
        console.log("Suspend user:", member);
        // API call: suspendMember(member.id)
      },
    });
  }

  private handleRemoveMember(event: CustomEvent) {
    const member = event.detail as Member;
    this.openActionModal({
      title: "Remove User",
      description: `Are you sure you want to remove ${member.user?.name || member.user?.email} from the organization?`,
      confirmLabel: "Remove User",
      variant: "danger",
      infoText:
        "This member will be permanently removed from the organization and will immediately lose access to all projects, billing, and shared resources. They will need to be re-invited to regain access.",
      onConfirm: async () => {
        await removeMember(this.organizationId, member.user.id);
        this.loadMembers(
          this.membersPage,
          this.rowsPerPage,
          this.membersSearchTerm,
          this.memberFilters,
        );
      },
    });
  }

  private handleResendInvitation(event: CustomEvent) {
    const invitation = event.detail as Invitation;
    this.openActionModal({
      title: "Resend Invitation",
      description: `Are you sure you want to resend this invitation to ${invitation.invitee_email}?`,
      confirmLabel: "Resend",
      variant: "default",
      onConfirm: async () => {
        await resendInvitation(this.organizationId, invitation.id);
        this.loadInvitations(
          this.invitationsPage,
          this.rowsPerPage,
          this.invitationsSearchTerm,
          this.invitationFilters,
        );
      },
    });
  }

  private handleCancelInvitation(event: CustomEvent) {
    const invitation = event.detail as Invitation;
    this.openActionModal({
      title: "Cancel Invitation",
      description: `Are you sure you want to cancel the invitation for ${invitation.invitee_email}?`,
      confirmLabel: "Cancel Invitation",
      variant: "danger",
      infoText:
        "This invitation will be permanently revoked and the recipient will lose access to join the organization.",
      onConfirm: async () => {
        await cancelInvitation(this.organizationId, invitation.id);
        this.loadInvitations(
          this.invitationsPage,
          this.rowsPerPage,
          this.invitationsSearchTerm,
          this.invitationFilters,
        );
      },
    });
  }

  private renderContent() {
    return html`
      <div class="sb-member-management-shell">
        <div>
          <div class="sb-flex sb-flex-col sb-gap-8 sb-w-full">
            <div class="sb-flex sb-justify-between">
              <div>
                <rtg-typography-h level="h3">${this.title}</rtg-typography-h>
                <rtg-typography-muted>${this.description}</rtg-typography-muted>
              </div>
              ${this.mode === "MODAL"
                ? html`
                    <rtg-button
                      variant="ghost"
                      size="icon-sm"
                      part="modal-close-button"
                      @click=${this._handleClose}
                    >
                      ${closeIcon()}
                    </rtg-button>
                  `
                : nothing}
            </div>
            ${this.monitoringFlag
              ? html`<div>
                  <sb-monitoring-cards></sb-monitoring-cards>
                </div>`
              : nothing}
            <rtg-tabs defaultValue=${MONITORING_TABS.MEMBERS}>
              <div class="sb-flex sb-justify-between sb-items-center sb-w-full">
                <rtg-tabs-list variant="line">
                  <rtg-tabs-trigger value=${MONITORING_TABS.MEMBERS}
                    >${MONITORING_TABS.MEMBERS}</rtg-tabs-trigger
                  >
                  <rtg-tabs-trigger value=${MONITORING_TABS.INVITATIONS}
                    >${MONITORING_TABS.INVITATIONS}</rtg-tabs-trigger
                  >
                </rtg-tabs-list>
                <rtg-button
                  type="button"
                  variant="default"
                  size="default"
                  @click=${() =>
                    setTimeout(() => {
                      document.dispatchEvent(
                        new CustomEvent("open-sidedrawer", {
                          detail: { id: this.inviteDrawerId, side: "right" },
                          bubbles: true,
                          composed: true,
                        }),
                      );
                    }, 50)}
                >
                  ${plusIcon()}Invite Members
                </rtg-button>
              </div>
              <div class="sb-mt-8">
                <rtg-tabs-content value=${MONITORING_TABS.MEMBERS}>
                  <sb-member-management-members-panel
                    .members=${this.members}
                    .membersLoading=${this.membersLoading}
                    .searchTerm=${this.membersSearchTerm}
                    .memberFilters=${this.memberFilters}
                    .availableRoles=${this.availableRoles}
                    .membersPage=${this.membersPage}
                    .membersTotalPages=${this.membersTotalPages}
                    .membersTotal=${this.membersTotal}
                    .rowsPerPage=${this.rowsPerPage}
                    .isLoadingRoles=${this.isLoadingRoles}
                    @members-search-change=${this.handleMembersSearchChange}
                    @members-page-change=${this.handleMembersPageChange}
                    @members-refresh=${() => this.handleRefresh("members")}
                    @members-filter-change=${this.handleMembersFilterChange}
                    @members-clear-filters=${this.handleMembersClearFilters}
                    @members-remove-filter=${this.handleMembersRemoveFilter}
                    @members-rows-per-page-change=${this
                      .handleMembersRowsPerPageChange}
                    @manage-member-roles=${this.openManageRoles}
                    @suspend-member=${this.handleSuspendMember}
                    @remove-member=${this.handleRemoveMember}
                  ></sb-member-management-members-panel>
                </rtg-tabs-content>

                <rtg-tabs-content value=${MONITORING_TABS.INVITATIONS}>
                  <sb-member-management-invitations-panel
                    .invitations=${this.invitations}
                    .invitationsLoading=${this.invitationsLoading}
                    .searchTerm=${this.invitationsSearchTerm}
                    .invitationFilters=${this.invitationFilters}
                    .availableRoles=${this.availableRoles}
                    .statusOptions=${this.invitationStatusOptions}
                    .invitationsPage=${this.invitationsPage}
                    .invitationsTotalPages=${this.invitationsTotalPages}
                    .invitationsTotal=${this.invitationsTotal}
                    .rowsPerPage=${this.rowsPerPage}
                    .isLoadingRoles=${this.isLoadingRoles}
                    @invitations-search-change=${this
                      .handleInvitationsSearchChange}
                    @invitations-page-change=${this.handleInvitationsPageChange}
                    @invitations-refresh=${() =>
                      this.handleRefresh("invitations")}
                    @invitations-filter-change=${this
                      .handleInvitationsFilterChange}
                    @invitations-clear-filters=${this
                      .handleInvitationsClearFilters}
                    @invitations-remove-filter=${this
                      .handleInvitationsRemoveFilter}
                    @invitations-rows-per-page-change=${this
                      .handleInvitationsRowsPerPageChange}
                    @resend-invitation=${this.handleResendInvitation}
                    @cancel-invitation=${this.handleCancelInvitation}
                  ></sb-member-management-invitations-panel>
                </rtg-tabs-content>
              </div>
            </rtg-tabs>
          </div>
        </div>
        <div class="sb-member-management-watermark-container">
          <sb-watermark custom-id="sb-watermark"></sb-watermark>
        </div>
      </div>
      <sb-invite-members-form
        organizationId=${this.organizationId}
        .drawerId=${this.inviteDrawerId}
      ></sb-invite-members-form>
      <sb-manage-roles-form
        .member=${this.selectedMember}
        organizationId=${this.organizationId}
        .drawerId=${this.manageRolesDrawerId}
      ></sb-manage-roles-form>
      <sb-members-action-modal
        .open=${this.actionModalOpen}
        .title=${this.actionModalConfig.title}
        .description=${this.actionModalConfig.description}
        .confirmLabel=${this.actionModalConfig.confirmLabel}
        .cancelLabel=${this.actionModalConfig.cancelLabel}
        .variant=${this.actionModalConfig.variant}
        .onConfirm=${this.actionModalConfig.onConfirm}
        .onCancel=${() => {
          this.actionModalOpen = false;
        }}
        .infoText=${this.actionModalConfig.infoText}
        @modal-confirmed=${() => {
          this.actionModalOpen = false;
        }}
        @modal-closed=${() => {
          this.actionModalOpen = false;
        }}
      ></sb-members-action-modal>
    `;
  }

  render() {
    if (this.mode === "MODAL") {
      if (!this.isOpen) return nothing;

      return html`
        <sb-modal
          .showClose=${false}
          .isOpen=${this.isOpen}
          @close=${this._handleClose}
          part="with_min_width"
        >
          ${this.renderContent()}
        </sb-modal>
      `;
    }

    return this.renderContent();
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define("sb-member-management", SbMemberManagement);

declare global {
  interface HTMLElementTagNameMap {
    "sb-member-management": SbMemberManagement;
  }
}
